import React, { Component } from 'react';
import PropTypes from 'prop-types';
import config from '../../config';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// The Turnstile script is a singleton on the page. Share one load promise so
// remounting the signup form never injects a second <script>.
let scriptPromise = null;

const loadTurnstileScript = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Turnstile requires a browser environment'));
  }
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      // Allow a later mount to retry rather than caching the failure forever.
      scriptPromise = null;
      reject(new Error('Failed to load Turnstile'));
    };
    if (!existing) {
      document.head.appendChild(script);
    }
  });

  return scriptPromise;
};

/**
 * Renders the Cloudflare Turnstile widget and reports its token upward.
 *
 * Renders nothing when no site key is configured, which is how captcha is
 * switched off per environment. `onToken(null)` signals that no usable token
 * is currently held, so the caller can keep the submit button disabled.
 */
class TurnstileField extends Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.widgetId = null;
    this.state = { loadFailed: false };
  }

  componentDidMount() {
    if (!config.turnstileSiteKey) {
      return;
    }

    loadTurnstileScript()
      .then(turnstile => {
        if (!this.containerRef.current || this.widgetId !== null) {
          return;
        }
        this.widgetId = turnstile.render(this.containerRef.current, {
          sitekey: config.turnstileSiteKey,
          callback: token => this.props.onToken(token),
          'expired-callback': () => this.props.onToken(null),
          'error-callback': () => this.props.onToken(null),
        });
      })
      .catch(() => {
        // Without the script there is no widget and no token, so the submit
        // button stays disabled. Say why rather than leaving the user stuck.
        this.props.onToken(null);
        this.setState({ loadFailed: true });
      });
  }

  componentDidUpdate(prevProps) {
    // A Turnstile token is single use, so a failed signup attempt burns it.
    // Hand the user a fresh challenge whenever the attempt it was issued for
    // has come back with an error.
    const shouldReset =
      this.props.resetSignal !== prevProps.resetSignal &&
      this.widgetId !== null &&
      typeof window !== 'undefined' &&
      window.turnstile;

    if (shouldReset) {
      window.turnstile.reset(this.widgetId);
      this.props.onToken(null);
    }
  }

  componentWillUnmount() {
    if (this.widgetId !== null && typeof window !== 'undefined' && window.turnstile) {
      window.turnstile.remove(this.widgetId);
      this.widgetId = null;
    }
  }

  render() {
    if (!config.turnstileSiteKey) {
      return null;
    }
    const { className, errorClassName, loadErrorMessage } = this.props;
    return (
      <div className={className}>
        <div ref={this.containerRef} />
        {this.state.loadFailed && loadErrorMessage ? (
          <div className={errorClassName}>{loadErrorMessage}</div>
        ) : null}
      </div>
    );
  }
}

TurnstileField.defaultProps = {
  className: null,
  errorClassName: null,
  loadErrorMessage: null,
  resetSignal: null,
};

TurnstileField.propTypes = {
  className: PropTypes.string,
  errorClassName: PropTypes.string,
  // Shown when Cloudflare's script cannot be reached at all.
  loadErrorMessage: PropTypes.node,
  onToken: PropTypes.func.isRequired,
  // Any value that changes identity when the widget should be reset.
  resetSignal: PropTypes.any,
};

export default TurnstileField;
